/**
 * Constants for auto-save functionality
 */

/**
 * Default debounce delay in milliseconds
 */
export const DEFAULT_AUTOSAVE_DEBOUNCE_MS = 500;

/**
 * Resource names for auto-save storage keys
 */
export const AUTOSAVE_RESOURCES = {
    EMPLOYEE: 'employee',
    CENTER: 'center',
    CHILD: 'child'
} as const;

/**
 * Type for auto-save resource names
 */
export type AutoSaveResource = (typeof AUTOSAVE_RESOURCES)[keyof typeof AUTOSAVE_RESOURCES];

/**
 * Generates a consistent storage key for auto-save
 * Unified pattern for both create and edit modes
 *
 * @param { AutoSaveResource } resource - Resource name from AUTOSAVE_RESOURCES
 * @param { string | number } [id] - Record ID for edit mode, omit for create mode
 * @returns { string } Storage key in format: 'resource-form-new' or 'resource-form-123'
 *
 */
export const getAutoSaveKey = (resource: AutoSaveResource, id?: string | number): string => {
    return `${resource}-form-${id || 'new'}`;
};

/**
 * Delay in milliseconds before attempting to restore form data
 * This ensures the form is fully initialized before restoration
 */
export const FORM_INIT_DELAY_MS = 100;

/**
 * Delay in milliseconds to wait after restoring data before re-enabling auto-save
 * This prevents the restoration itself from triggering another save
 */
export const POST_RESTORE_DELAY_MS = 100;

/**
 * Type for stored auto-save data
 */
export interface AutoSaveStoredData {
    data: Record<string, unknown>;
    timestamp: string;
}
